import ABCyaPhaser from "@abcya/abcya_phaser";
import Phaser from "phaser";
import CellGroup, { CellGroupEvents } from "../components/CellGroup";
import NavBar, { NavEvents } from "../components/NavBar";
import gameData from "../gameData";
import SoundAssetKeys from "../SoundAssetKeys";
import UIAssetKeys from "../UIAssetKeys";
import Level from "./Level";
import ShelfPack from "@mapbox/shelf-pack";
import Instructions, { InstructionsEvents } from "../components/Instructions";
import { LevelDataEvents } from "../LevelData";
import { CellStateIds } from "../components/Cell";
import SoundController, { playGameJingle } from "../SoundController";
import LocalizationUIAssets from "../Localization-UIAssets";

const Constants = {
  IN_TWEEN: 250,
  INSTRUCTIONS_IN_TWEEN: 500,
  INSTRUCTIONS_OUT_TWEEN: 250,
};

const PieceDisplays = [
  "AnswerTileDarkBlue",
  "AnswerTileGreen",
  "AnswerTileLightBlue",
  "AnswerTileOrange",
  "AnswerTilePink",
  "AnswerTilePurple",
  "AnswerTileRedOrange",
  "AnswerTileYellow",
];

export default class Game extends Phaser.Scene {
  static get KEY() {
    return "Game";
  }

  preload() {
    // preload your assets here, if needed
  }

  create() {
    this.bg = null;
    this.navBar = null;
    this.gameSound = null;
    this.puzzle = null;
    this.pieces = [];
    this.enabled = false;

    this.bg = ABCyaPhaser.Images.addImage(
      0,
      0,
      UIAssetKeys._GameBackground,
      this
    );
    this.bg.setOrigin(0, 0);

    this.navBar = new NavBar(this);
    this.navBar.y = -100;
    this.add.existing(this.navBar);

    this.navBar.enableGameButtons();
    this.navBar.enableSoundButton();
    this.navBar.enableMenuButton();
    this.navBar.enableLevelText(true);

    this.navBar.emitter.on(
      NavEvents.RESET_SELECTED,
      (event) => {
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._Click, this);
        this.removeInstructions();
        this.setupLevel();
      },
      this
    );

    this.navBar.emitter.on(
      NavEvents.HOME_SELECTED,
      (event) => {
        this.disable();
        this.destroyInstructions();
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._Click, this);
        this.scene.start(Level.KEY);
      },
      this
    );

    this.navBar.emitter.on(
      NavEvents.HELP_SELECTED,
      (event) => {
        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._Click, this);
        this.showInstructions(true, 0);
      },
      this
    );

    this.leftBar = ABCyaPhaser.Images.addImage(
      -310,
      104,
      UIAssetKeys._AnswerAreaLeft,
      this
    );
    this.leftBar.setOrigin(0, 0);

    this.rightBar = ABCyaPhaser.Images.addImage(
      this.scale.width + 317,
      104,
      UIAssetKeys._AnswerAreaLeft,
      this
    );
    this.rightBar.setOrigin(0, 0);
    this.rightBar.scaleX = -1;

    SoundController.initScene(this);
    this.makeParticles();
    this.setupLevel();

    ABCyaPhaser.Tweens.addTween(
      this.leftBar,
      Constants.IN_TWEEN,
      {
        x: -7,
      },
      Phaser.Math.Easing.Circular.Out
    );

    ABCyaPhaser.Tweens.addTween(
      this.rightBar,
      Constants.IN_TWEEN,
      {
        x: this.scale.width + 7,
      },
      Phaser.Math.Easing.Circular.Out
    );

    ABCyaPhaser.Tweens.addTween(
      this.navBar,
      Constants.IN_TWEEN,
      {
        y: 0,
      },
      Phaser.Math.Easing.Circular.Out
    );
  }

  setupLevel() {
    if (!this.instructions) {
      this.tweens.killAll();
    }
    this.disable();
    if (this.tryPrompt) {
      this.tryPrompt.destroy();
      this.tryPrompt = null;
    }
    if (this.winPrompt) {
      this.winPrompt.destroy();
      this.winPrompt = null;
    }
    this.navBar.setLevelText(
      gameData.levelData.getCurrentLevelIndex() + 1,
      gameData.levelData.getTotalLevels()
    );
    this.makePuzzle(gameData.levelData.getCurrentLevelData().puzzle);
    this.makePieces(gameData.levelData.getCurrentLevelData().pieces);
    if (gameData.levelData.getCurrentLevelIsLocked()) {
      gameData.levelData.unlockCurrentLevel();
      gameData.levelData.saveToLocal();
    }
    this.doPieceInTransitions();

    if (this.instructions) {
      this.children.bringToTop(this.instructions);
    }
  }

  makePuzzle(data) {
    if (this.puzzle !== null) {
      this.puzzle.destroy();
      this.puzzle = null;
    }

    this.puzzle = new CellGroup(this, data);
    this.puzzle.x = this.scale.width * 0.5;
    this.puzzle.y = this.scale.height * 0.5;
    this.add.existing(this.puzzle);
  }

  makePieces(data) {
    for (let i = 0; i < this.pieces.length; i++) {
      const piece = this.pieces[i];
      piece.destroy();
      piece = null;
    }
    this.pieces = [];

    let displayIndex = 0;
    for (let n = 0; n < data.length; n++) {
      const display = PieceDisplays[displayIndex];
      displayIndex++;
      if (displayIndex >= PieceDisplays.length) {
        displayIndex = 0;
      }
      const piece = new CellGroup(this, data[n], display, null, 0xffffff, true);
      this.add.existing(piece);
      this.pieces.push(piece);
    }

    const middleIndex = Math.round(this.pieces.length * 0.5);
    const leftRect = { x: 50, y: 250, width: 300, height: 500 };
    const rightRect = {
      x: 799,
      y: 250,
      width: 300,
      height: 500,
    };
    this.packPieces(this.pieces.slice(0, middleIndex), leftRect);
    this.packPieces(this.pieces.slice(middleIndex), rightRect);
  }

  packPieces(pieces, rect) {
    // https://github.com/mapbox/shelf-pack

    const sprite = new ShelfPack(rect.width, rect.height);
    const requests = [];
    for (let i = 0; i < pieces.length; i++) {
      const pieceBounds = pieces[i].getBounds();
      requests[i] = {
        id: i,
        w: pieceBounds.width + 20,
        h: pieceBounds.height + 20,
      };
    }

    const results = sprite.pack(requests);
    results.forEach(function (bin) {
      var piece = pieces[bin.id];
      var bounds = piece.getBounds();
      piece.x =
        rect.x +
        bin.x +
        (bounds.width > 250 ? bounds.width * 0.35 : bounds.width * 0.5);
      piece.y = rect.y + bin.y;
    });
  }

  doPieceInTransitions() {
    this.puzzle.emitter.on(
      CellGroupEvents.TRANSITION_IN_COMPLETE,
      (event) => {
        if (
          !gameData.levelData.getInstructionsHaveBeenSeen() &&
          gameData.levelData.getCurrentLevelIndex() === 0
        ) {
          this.showInstructions(true, Constants.IN_TWEEN);
        } else {
          this.enable();
        }
      },
      this
    );
    for (let i = 0; i < this.pieces.length; i++) {
      const piece = this.pieces[i];
      piece.originalPosition = new Phaser.Geom.Point(piece.x, piece.y);
      piece.doInTransition(0);
    }
    this.puzzle.doInTransition(Constants.IN_TWEEN);
  }

  showInstructions(useCloseButton, delay) {
    if (this.instructions) {
      return;
    }

    this.instructions = new Instructions(this, useCloseButton);
    this.instructions.x = this.scale.width * 0.5;
    this.instructions.y = this.scale.height * 0.5;
    this.instructions.scaleX = this.instructions.scaleY = 0.75;
    this.instructions.alpha = 0;
    this.add.existing(this.instructions);

    ABCyaPhaser.Tweens.addTween(
      this.instructions,
      Constants.INSTRUCTIONS_IN_TWEEN,
      {
        delay: delay,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
      },
      Phaser.Math.Easing.Cubic.Out,
      () => {
        this.instructions.emitter.on(InstructionsEvents.CLOSED, () => {
          this.removeInstructions();
        });
      }
    );
    gameData.levelData.setInstructionsHaveBeenSeen(true);
  }

  removeInstructions() {
    if (this.instructions) {
      this.instructions.emitter.removeAllListeners();
      ABCyaPhaser.Tweens.addTween(
        this.instructions,
        Constants.INSTRUCTIONS_OUT_TWEEN,
        {
          alpha: 0,
          scaleX: 0.75,
          scaleY: 0.75,
        },
        Phaser.Math.Easing.Cubic.Out,
        () => {
          this.destroyInstructions();
          this.enable();
        }
      );
    }
  }

  destroyInstructions() {
    if (this.instructions) {
      this.instructions.emitter.removeAllListeners();

      this.instructions.destroy();
      this.instructions = null;
    }
  }

  enable() {
    if (this.enabled) {
      return;
    }

    gameData.levelData.emitter.on(LevelDataEvents.LEVEL_CHANGED, () => {
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._Click, this);
      this.setupLevel();
    });

    this.navBar.enableResetButton();
    this.navBar.enableHelpButton();

    for (let n = 0; n < this.pieces.length; n++) {
      const piece = this.pieces[n];

      ABCyaPhaser.Container.setContainerInteractive(piece);

      this.input.setDraggable(piece);

      piece.on("drag", (pointer, dragX, dragY, gameObject) => {
        this.destroyInstructions();
        piece.clearOverlays();
        piece.x = dragX;
        piece.y = dragY;
        const collisions = this.puzzle.checkCollisions(piece);

        if (collisions.length > 0) {
          for (let i = 0; i < collisions.length; i++) {
            collisions[i].cell1.showValueOverlay(
              collisions[i].cell2.getValue()
            );
          }
        }
      });

      piece.on(
        Phaser.Input.Events.GAMEOBJECT_POINTER_UP,
        (event) => {
          let p = piece;
          const collisions = this.puzzle.checkCollisions(p, true);
          if (collisions.length > 0) {
            ABCyaPhaser.Audio.playOneShot(
              SoundAssetKeys._Placing_Puzzle_Piece,
              this
            );

            p.removeAllListeners();
            this.pieces.splice(this.pieces.indexOf(p), 1);
            p.destroy();
            p = null;

            const delayTime = 100;
            const tweens = [];
            for (let i = 0; i < collisions.length; i++) {
              collisions[i].cell2.emphasizeDisplay(i * delayTime);
              const collisionCoords = collisions[i].cell2.getBounds();
              if (i > 0) {
                const tween = ABCyaPhaser.Tweens.createTween(
                  this.particleFollower,
                  75,
                  {
                    x: collisionCoords.x + collisionCoords.width * 0.5,
                    y: collisionCoords.y + collisionCoords.height * 0.5,
                  }
                );
                tweens.push(tween);
              } else {
                this.particleFollower.x =
                  collisionCoords.x + collisionCoords.width * 0.5;
                this.particleFollower.y =
                  collisionCoords.y + collisionCoords.height * 0.5;
              }
            }

            if (tweens.length > 0) {
              this.particleEmitter.start();
              this.tweens.timeline({
                tweens: tweens,
                onComplete: () => {
                  this.particleEmitter.stop();
                },
              });
            } else {
              this.particleEmitter.emitParticle(10);
            }

            if (this.checkForWin()) {
              gameData.levelData.unlockNextLevel();
              gameData.levelData.saveToLocal();
              this.showWinPrompt();
              this.puzzle.doOutTransition(0);
            } else if (this.pieces.length < 1) {
              this.showTryAgainPrompt();
            }
          } else {
            ABCyaPhaser.Tweens.addTween(
              p,
              200,
              {
                x: p.originalPosition.x,
                y: p.originalPosition.y,
              },
              Phaser.Math.Easing.Cubic.Out
            );
          }
        },
        this
      );
    }
    this.enabled = true;
  }

  checkForWin() {
    const puzzleCells = this.puzzle.getAllCells();
    for (let i = 0; i < puzzleCells.length; i++) {
      if (puzzleCells[i].getState() !== CellStateIds.CLEARED) {
        return false;
      }
    }
    return true;
  }

  showWinPrompt() {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._Level_Complete, this);

    this.winPrompt = ABCyaPhaser.Images.addImage(
      this.scale.width * 0.5,
      this.scale.height * 0.5,
      LocalizationUIAssets._GoodJobPopup,
      this,
      ABCyaPhaser.Images.LOCALIZATION_ATLAS
    );
    this.winPrompt.scaleX = 0.75;
    this.winPrompt.scaleY = 0.75;
    this.winPrompt.alpha = 0;

    ABCyaPhaser.Tweens.addTween(
      this.winPrompt,
      1000,
      {
        delay: 500,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
      },
      Phaser.Math.Easing.Cubic.Out,
      () => {
        ABCyaPhaser.Tweens.addTween(
          this.winPrompt,
          250,
          {
            delay: 400,
            alpha: 0,
            scaleX: 0.9,
            scaleY: 0.9,
          },
          Phaser.Math.Easing.Cubic.In,
          () => {
            this.winPrompt.destroy();
            this.winPrompt = null;
            if (!gameData.levelData.nextLevel()) {
              this.gameComplete();
            }
          }
        );
      }
    );
  }

  showTryAgainPrompt() {
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._Try_Again, this);

    this.tryPrompt = ABCyaPhaser.Images.addImage(
      this.scale.width * 0.5,
      this.scale.height * 0.85,
      LocalizationUIAssets._TryAgainText,
      this,
      ABCyaPhaser.Images.LOCALIZATION_ATLAS
    );
    this.tryPrompt.scaleX = 0.75;
    this.tryPrompt.scaleY = 0.75;
    this.tryPrompt.alpha = 0;
    ABCyaPhaser.Tweens.addTween(
      this.tryPrompt,
      1000,
      {
        delay: 500,
        alpha: 1,
        scaleX: 1,
        scaleY: 1,
      },
      Phaser.Math.Easing.Cubic.Out
    );
  }

  gameComplete() {
    this.destroyInstructions();
    this.scene.start(Level.KEY);
  }

  disable() {
    gameData.levelData.emitter.removeAllListeners();
    this.navBar.disableResetButton();
    this.navBar.disableHelpButton();
    for (let i = 0; i < this.pieces.length; i++) {
      this.pieces[i].removeAllListeners();
    }
    this.enabled = false;
  }

  makeParticles() {
    this.particles = this.add.particles(
      ABCyaPhaser.Images.DEFAULT_ATLAS,
      UIAssetKeys._particle
    );

    this.particleFollower = { x: 0, y: 0 };
    this.particleEmitter = this.particles.createEmitter({
      follow: this.particleFollower,
      lifespan: [200, 1500],
      speed: 150,
      blendMode: "ADD",
      scale: { start: 0.8, end: 0 },
      alpha: { start: 1, end: 0 },
      frequency: 1,
      quantity: 5,
    });
    this.particleEmitter.stop();
  }
}
